import { KEYPAD_ISSUE, KEYPAD_SEVERITY, advisory } from './catalog/issue';
import type { KeypadIssue } from './catalog/issue';
import { KEY_STATE_SPECS } from './catalog/keyState';
import { DEVICE_ANSWER } from './catalog/status';
import { panelAdvisories } from './commands/keypads';
import type { EncodedKeypad } from './encode';
import { keyNumber } from './model';
import type { Keypad, KeypadBook } from './model';
import type { KeyStateReport } from './stored';
import type { MappingTarget } from './targets';

/**
 * Everything standing between a panel and a correct write.
 *
 * One list the console can show as-is, assembled from three sources that mean
 * different things: the panel's own shape, the states this console cannot spell,
 * and what the device holds that this console no longer means to write. Blocking
 * issues stop a state from being written; advisories are worth a look and do not.
 */
export interface PanelValidation {
  readonly keypad: Keypad;
  readonly issues: readonly KeypadIssue[];
  readonly blocking: readonly KeypadIssue[];
  /** The states that would be written right now. */
  readonly states: number;
  /** True when at least one state drives something. */
  readonly writeable: boolean;
}

export const validatePanel = (input: {
  readonly book: KeypadBook;
  readonly keypad: Keypad;
  readonly targets: readonly MappingTarget[];
  readonly encoded: EncodedKeypad;
  readonly reports: readonly KeyStateReport[];
}): PanelValidation => {
  const issues: KeypadIssue[] = [
    ...panelAdvisories(input.book, input.keypad),
    ...input.encoded.issues,
    ...leftoverIssues(input.reports),
    ...emptyWriteIssues(input.encoded),
  ];
  return {
    keypad: input.keypad,
    issues,
    blocking: issues.filter((issue) => issue.severity === KEYPAD_SEVERITY.BLOCKING),
    states: input.encoded.entries.length,
    writeable: input.encoded.entries.length > 0,
  };
};

/** The panel validation for a book, for the console's own summary. */
export const countBlocking = (validations: readonly PanelValidation[]): number =>
  validations.reduce((total, panel) => total + panel.blocking.length, 0);

/** The panel with work outstanding, or undefined when every panel is clean. */
export const firstBlocked = (
  validations: readonly PanelValidation[],
): PanelValidation | undefined => validations.find((panel) => panel.blocking.length > 0);

/**
 * Key states the device holds a mapping for while this console drives nothing
 * there. Reading it back is the only way to know, so it is said as what it is:
 * the device has something, and writing this panel will not clear it.
 */
const leftoverIssues = (reports: readonly KeyStateReport[]): readonly KeypadIssue[] =>
  reports
    .filter(
      (report) =>
        report.encoded === null &&
        (report.answer.kind === DEVICE_ANSWER.MAPPING ||
          report.answer.kind === DEVICE_ANSWER.UNREADABLE),
    )
    .map((report) =>
      advisory(
        KEYPAD_ISSUE.DEVICE_LEFTOVER,
        `Button ${keyNumber(report.key)} still holds a mapping on the device for its ${KEY_STATE_SPECS[report.state].label.toLowerCase()}.`,
      ),
    );

/** A panel with nothing programmed cannot be written at all. */
const emptyWriteIssues = (encoded: EncodedKeypad): readonly KeypadIssue[] =>
  encoded.entries.length === 0
    ? [advisory(KEYPAD_ISSUE.NOTHING_TO_WRITE, 'No key state of this panel drives an output yet.')]
    : [];
